import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Link as LinkIcon, MessageCircle, Trash2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShareSetupLinkDialog({ open, onOpenChange, school }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !school?.schoolId) { setToken(null); return; }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/setup-tokens/by-school/${school.schoolId}`)
      .then((r) => { if (!cancelled) setToken(r.data || null); })
      .catch(() => { if (!cancelled) setToken(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, school?.schoolId]);

  const generate = async () => {
    if (!school?.schoolId) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/setup-tokens`, { schoolId: school.schoolId });
      setToken(r.data);
      toast.success("Setup link ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create link");
    } finally { setLoading(false); }
  };

  const regenerate = async () => {
    if (!token?.token) return;
    if (!window.confirm("Generate a brand new link? The current link will stop working.")) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/setup-tokens/${token.token}`);
      const r = await axios.post(`${API}/setup-tokens`, { schoolId: school.schoolId });
      setToken(r.data);
      toast.success("New link generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const publicUrl = token?.token
    ? `${window.location.origin}/setup-form/${token.token}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press to copy");
    }
  };

  const whatsappShare = () => {
    if (!publicUrl) return;
    // Odia outreach (admin-supplied) — embeds the school's unique public
    // setup-form URL in the "Online" path, and pre-fills the WhatsApp chat
    // with the headmaster so they receive the same motivating message.
    const lines = [
      "⚠️ ସ୍କୁଲ୍ ସେଟଅପ୍ ଅଧୁରା ଅଛି!",
      "",
      "ନମସ୍କାର ଶିକ୍ଷକ ବନ୍ଧୁ,",
      "ଆପଣଙ୍କ ସ୍କୁଲ୍ ସେଟଅପ୍ ସରିନଥିବାରୁ ଆପଣ ବିଲ୍ ପ୍ରସ୍ତୁତ କରିପାରିବେ ନାହିଁ। ୧୫ ମେ' ଡେଡ୍‌ଲାଇନ୍ ପୂର୍ବରୁ ତୁରନ୍ତ ଏହି ଦୁଇଟିରୁ ଗୋଟିଏ କରନ୍ତୁ:",
      "",
      "1️⃣ Online: ଲିଙ୍କ୍ କ୍ଲିକ୍ କରି ଡାଟା ଏଣ୍ଟ୍ରି ସାରନ୍ତୁ।",
      `👉 ${publicUrl}`,
      "",
      "2️⃣ WhatsApp: ୱେବସାଇଟ୍‌ରୁ ଫର୍ମ ଡାଉନଲୋଡ୍ କରି, ଫିଲପ୍ କରନ୍ତୁ ଓ ଫଟୋ ପଠାନ୍ତୁ। ଆମେ ଆପଣଙ୍କ ଆକାଉଣ୍ଟ Activate କରିଦେବୁ।",
      "",
      "ସମୟ ବଞ୍ଚାନ୍ତୁ, ଟେନସନ୍ ମୁକ୍ତ ହୁଅନ୍ତୁ!",
      "📞 WhatsApp: 8093414633",
      "ଟିମ୍ MDM ସହାୟକ",
    ];
    const text = encodeURIComponent(lines.join("\n"));
    const phone = school?.mobile && /^\d{10}$/.test(school.mobile) ? `91${school.mobile}` : "";
    const wa = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-setup-link-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LinkIcon className="h-4 w-4 text-clay" /> Share setup link</DialogTitle>
          <DialogDescription>
            {school?.schoolName} · UDISE {school?.udiseCode}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-ink-700">Loading…</p>
        ) : !token ? (
          <div className="space-y-3">
            <p className="rounded-lg bg-ochre/10 px-3 py-2 text-xs text-ink-700">
              No setup link yet. Generate one to share via WhatsApp — the school can fill their details on mobile without logging in.
              The link never expires and submissions appear in <b>Setup Reviews</b> for your approval.
            </p>
            <Button onClick={generate} data-testid="share-setup-generate-btn" className="w-full gap-2 bg-clay hover:bg-clay/90">
              <LinkIcon className="h-4 w-4" /> Generate setup link
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-sand-50 p-3">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-ink-700">Public link</p>
              <p className="mt-1 break-all font-mono text-xs text-ink-900" data-testid="share-setup-link-url">{publicUrl}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={copy} data-testid="share-setup-copy-btn" className="gap-1.5">
                <Copy className="h-3.5 w-3.5" /> Copy
              </Button>
              <Button onClick={whatsappShare} data-testid="share-setup-whatsapp-btn" className="gap-1.5 bg-moss text-white hover:bg-moss-dark">
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={regenerate} data-testid="share-setup-regenerate-btn" className="w-full gap-1.5 text-ink-700">
              <RefreshCw className="h-3.5 w-3.5" /> Regenerate (invalidates old link)
            </Button>
            <p className="text-[0.7rem] text-ink-700">
              Tip: submissions appear in the <b>Setup Reviews</b> tab. Approve to apply the data.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
